import { useState, useEffect } from 'react';
import {
  useForm,
  useFormState,
  useFieldArray,
  Controller,
} from 'react-hook-form';

import { Confirmation, FormPublishingFooter } from '@components';
import { datamodelTypeLookup, fieldTypeLookup } from '@constants';
import {
  FormFieldset,
  ImageItemsField,
  ItemsField,
  MultiSelectField,
  SelectField,
  StepperField,
  TextField,
  ToggleField,
} from '@Forms/fields.jsx';
import {
  slugifyDash,
  slugifyUnderscore,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@svgs';

const DataModelField = ({
  datamodelData,
  datamodelType,
  fieldsArray,
  groupsData,
  index,
  datamodelsData,
  useFormReturn,
  field,
}) => {
  // datamodel field component
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    control,
    reset,
    watch,
  } = useFormReturn;

  // Remove Data Model Confirmation Modal
  const [showConfirmation, setShowConfirmation] = useState({
    display: false,
    target: null,
    remove: {},
  });
  const toggleModal = (target, remove) => {
    setShowConfirmation({ display: true, target: target, remove: remove });
  };

  const fieldPrefix = `fields.${index}`;

  // state used based on the currently selected value of the field type
  const [fieldType, setFieldType] = useState(null);

  useEffect(() => {
    // set the fieldType based on the current type
    setFieldType(getValues(`${fieldPrefix}.field_type`));
  });

  // change event on field type
  const fieldTypeChange = (e) => {
    setFieldType(e.target.value);

    if (!['image'].includes(e.target.value)) {
      // disable alt tag toggle if switched away from image
      reset({
        [`${fieldPrefix}.enable_alt_tag`]: false,
      });
    }
    if (!['image_select', 'select'].includes(e.target.value)) {
      // if field type does not have choices, empty its value
      reset({
        [`${fieldPrefix}.choices`]: [],
      });
    }
    if (!['related_model_list'].includes(e.target.value)) {
      // if field type does not have related_datamodel_id, set to null
      reset({
        [`${fieldPrefix}.related_datamodel_id`]: null,
      });
    }
  };

  // datamodel item type choices for dropdown
  let datamodelTypeChoices = datamodelsData
    ? datamodelsData.objects
        .filter((d) => d.datamodel_type == 'datamodel_item')
        .map((d) => {
          return (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          );
        })
    : [];

  // related datamodel choices for dropdown
  let relatedDatamodelsChoices = datamodelsData
    ? datamodelsData.objects
        .filter((d) => d.datamodel_type == 'related_datamodel')
        .map((d) => {
          return (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          );
        })
    : [];

  // list of options to pass to react-select
  let groupOptions = groupsData
    ? groupsData.objects
        .filter((group) => group.settings?.restrict_custom_fields)
        .map((group) => {
          return { value: group.id, label: group.name };
        })
    : [];

  return (
    <div className="sb-form-item-inner">
      <div className="sb-form-item-elements">
        <fieldset>
          <TextField
            register={register}
            error={errors.fields?.[index]?.label}
            options={{ required: 'This field is required' }}
            name={`${fieldPrefix}.label`}
            label="Label"
          />
          <SelectField
            register={register}
            error={errors.fields?.[index]?.field_type}
            options={{
              required: 'This field is required',
              onChange: fieldTypeChange,
            }}
            name={`${fieldPrefix}.field_type`}
            label="Field type"
          >
            <option value="">Select field type...</option>
            <option value="short_text">{fieldTypeLookup.short_text}</option>
            <option value="long_text">{fieldTypeLookup.long_text}</option>
            <option value="multi_line_text">
              {fieldTypeLookup.multi_line_text}
            </option>
            <option value="simple_html">{fieldTypeLookup.simple_html}</option>
            <option value="html">{fieldTypeLookup.html}</option>
            <option value="slug">{fieldTypeLookup.slug}</option>
            <option value="boolean">{fieldTypeLookup.boolean}</option>
            <option value="rgba_color">{fieldTypeLookup.rgba_color}</option>
            <option value="image">{fieldTypeLookup.image}</option>
            <option value="date_picker">{fieldTypeLookup.date_picker}</option>
            <option value="datetime_picker">
              {fieldTypeLookup.datetime_picker}
            </option>
            {datamodelType != 'related_datamodel' && (
              <option value="related_model_list">
                {fieldTypeLookup.related_model_list}
              </option>
            )}
            <option value="select">{fieldTypeLookup.select}</option>
            <option value="url">{fieldTypeLookup.url}</option>
            <option value="number">{fieldTypeLookup.number}</option>
            <option value="image_select">{fieldTypeLookup.image_select}</option>
            <option value="datamodel_item">
              {fieldTypeLookup.datamodel_item}
            </option>
            <option value="stepper">{fieldTypeLookup.stepper}</option>
            <option value="blog_post_select">
              {fieldTypeLookup.blog_post_select}
            </option>
            <option value="address_autocomplete">
              {fieldTypeLookup.address_autocomplete}
            </option>
            <option value="item_list">{fieldTypeLookup.item_list}</option>
          </SelectField>
          {['stepper'].includes(fieldType) && (
            <StepperField
              control={control}
              error={errors.fields?.[index]?.min_items}
              name={`${fieldPrefix}.count`}
              rules={{ required: 'This field is required' }}
              label="Count"
              helpText="Number of items to display."
            />
          )}
          {['datamodel_item'].includes(fieldType) && (
            <SelectField
              register={register}
              error={errors.fields?.[index]?.related_datamodel_id}
              name={`${fieldPrefix}.related_datamodel_id`}
              options={{ required: 'This field is required' }}
              label="Content item type"
              helpText="Associated content database type for this field."
            >
              <option value="">Select...</option>
              {datamodelTypeChoices}
            </SelectField>
          )}
          {['related_model_list'].includes(fieldType) && (
            <SelectField
              register={register}
              error={errors.fields?.[index]?.related_datamodel_id}
              name={`${fieldPrefix}.related_datamodel_id`}
              options={{ required: 'This field is required' }}
              label="Related datamodel"
              helpText="Associated related data model for this field."
            >
              <option value="">Select...</option>
              {relatedDatamodelsChoices}
            </SelectField>
          )}
          {['related_model_list'].includes(fieldType) && (
            <>
              <StepperField
                control={control}
                error={errors.fields?.[index]?.min_items}
                name={`${fieldPrefix}.min_items`}
                defaultValue={0}
                label="Min items"
                helpText="Minimum number of items that can be selected."
              />
              <StepperField
                control={control}
                error={errors.fields?.[index]?.max_items}
                name={`${fieldPrefix}.max_items`}
                defaultValue={0}
                label="Max items"
                helpText="Maximum number of items that can be selected."
              />
            </>
          )}
          {['select'].includes(fieldType) && (
            <ItemsField
              control={control}
              register={register}
              error={errors.fields?.[index]?.choices}
              name={`${fieldPrefix}.choices`}
              options={{ required: 'This field is required' }}
              label="Field choices"
              setValue={setValue}
            />
          )}
          {['image_select'].includes(fieldType) && (
            <ImageItemsField
              control={control}
              register={register}
              error={errors.fields?.[index]?.choices}
              name={`${fieldPrefix}.choices`}
              options={{ required: 'This field is required' }}
              label="Image choices"
              setValue={setValue}
              watch={watch}
            />
          )}
          {['image'].includes(fieldType) && (
            <ToggleField
              register={register}
              control={control}
              error={errors.fields?.[index]?.enable_alt_tag}
              name={`${fieldPrefix}.enable_alt_tag`}
              label="Enable alt tag"
              helpText="When enabled on the Image field, enables an Alt-tag field that can be used to enter an alt attribute value for the image."
            />
          )}
          <TextField
            register={register}
            error={errors.fields?.[index]?.help_text}
            name={`${fieldPrefix}.help_text`}
            label="Help text"
            helpText="Optional help text to show below the field"
          />
          <TextField
            register={register}
            error={errors.fields?.[index]?.placeholder_text}
            name={`${fieldPrefix}.placeholder_text`}
            label="Placeholder text"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.fields?.[index]?.required}
            name={`${fieldPrefix}.required`}
            label="Field is required"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.fields?.[index]?.translatable}
            name={`${fieldPrefix}.translatable`}
            label="Translatable"
          />
          <TextField
            register={register}
            options={{
              onChange: slugifyUnderscore,
              onBlur: (e) => {
                slugifyOnBlur(e, '_');
                setValue(`${fieldPrefix}.slug`, e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
            error={errors.fields?.[index]?.slug}
            name={`${fieldPrefix}.slug`}
            label="Slug"
          />
          {[
            'page_custom_fields',
            'site_custom_fields',
            'related_datamodel',
          ].includes(datamodelData.datamodel_type) && (
            <MultiSelectField
              control={control}
              error={errors.fields?.[index]?.groups}
              name={`${fieldPrefix}.groups`}
              label="Group visibility"
              options={groupOptions}
            />
          )}
          {/* Hidden order field needs to pass index */}
          <Controller
            control={control}
            name={`${fieldPrefix}.order`}
            defaultValue={index}
            render={({ field }) => (
              <input
                {...field}
                type="hidden"
                onChange={(val) => {
                  field.onChange(index);
                }}
                value={index}
              />
            )}
          />
        </fieldset>
      </div>
      <div className="sb-form-item-controls">
        <button
          type="button"
          className="sb-form-item-control sb-form-item-control-up"
          disabled={index === 0}
          onClick={() => fieldsArray.move(index, index - 1)}
        >
          <ChevronUpIcon />
        </button>
        <button
          type="button"
          className="sb-form-item-control sb-form-item-control-down"
          disabled={index === fieldsArray.fields.length - 1}
          onClick={() => fieldsArray.move(index, index + 1)}
        >
          <ChevronDownIcon />
        </button>
        <button
          type="button"
          className="sb-form-item-control sb-form-item-control-delete"
          onClick={() =>
            toggleModal(`${fieldPrefix}`, () => {
              fieldsArray.remove(index);
            })
          }
        >
          <TrashIcon />
        </button>
      </div>
      {showConfirmation.display &&
        showConfirmation.target === `${fieldPrefix}` && (
          <Confirmation
            showConfirmation={showConfirmation}
            setShowConfirmation={setShowConfirmation}
          />
        )}
    </div>
  );
};

export const DataModelForm = ({
  datamodelData,
  datamodelsData,
  disabled,
  groupsData,
  mutation,
}) => {
  const useFormReturn = useForm({
    defaultValues: datamodelData,
    mode: 'onBlur',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    setFocus,
    setValue,
    control,
    clearErrors,
    reset,
    watch,
  } = useFormReturn;
  const fieldsArray = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'fields', // unique name for your Field Array
  });
  const { isDirty, isValid } = useFormState({ control });

  // state used based on the currently selected value of the datamodel type
  const [datamodelType, setDatamodelType] = useState(null);

  useEffect(() => {
    // set focus to the name field if this is a new datamodel
    if (!datamodelData.id) {
      setFocus('name');
    }
  }, [setFocus]);

  useEffect(() => {
    // set the datamodelType based on the current type
    setDatamodelType(getValues(`datamodel_type`));
  }, []);

  const datamodelTypeChange = (e) => {
    // set the datamodelType on change to selection
    setDatamodelType(e.target.value);
  };

  const onSubmit = (data) => {
    // update nested group references before mutation
    data.fields = data.fields.map((field) => {
      return {
        ...field,
        groups: field.groups
          ? field.groups.map((groupId) => {
              return { group: { id: groupId } };
            })
          : [],
      };
    });
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  // Clear previous errors on each submit attempt so the user can re-submit
  // even if the last attempt set server-side form errors.
  const onSubmitAttempt = (e) => {
    e?.preventDefault?.();
    clearErrors();
    return handleSubmit(onSubmit)(e);
  };

  const fieldsList = fieldsArray.fields.map((field, index) => {
    // nested datamodel fields
    //
    // passing the datamodelType so it can be used to determine which options show
    return (
      <DataModelField
        key={field.id}
        useFormReturn={useFormReturn}
        datamodelData={datamodelData}
        datamodelType={datamodelType}
        datamodelsData={datamodelsData}
        field={field}
        fieldsArray={fieldsArray}
        index={index}
        groupsData={groupsData}
      />
    );
  });

  const blankField = () => {
    // blank datamodel field keys for new adds
    return {
      choices: [],
      enable_alt_tag: true,
      field_type: '',
      groups: '',
      help_text: '',
      label: '',
      placeholder_text: '',
      required: false,
      related_datamodel_id: null,
      slug: '',
      translatable: false,
    };
  };

  return (
    <form
      onSubmit={onSubmitAttempt}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          name="name"
          label="Name"
        />
        <SelectField
          register={register}
          error={errors.datamodel_type}
          options={{
            required: 'This field is required',
            onChange: datamodelTypeChange,
          }}
          name="datamodel_type"
          label="Type"
        >
          <option value="">Select a type...</option>
          <option value="site_custom_fields">
            {datamodelTypeLookup.site_custom_fields}
          </option>
          <option value="page_custom_fields">
            {datamodelTypeLookup.page_custom_fields}
          </option>
          <option value="location_custom_fields">
            {datamodelTypeLookup.location_custom_fields}
          </option>
          <option value="datamodel_item">
            {datamodelTypeLookup.datamodel_item}
          </option>
          <option value="related_datamodel">
            {datamodelTypeLookup.related_datamodel}
          </option>
          <option value="templatecomponent_fields">
            {datamodelTypeLookup.templatecomponent_fields}
          </option>
          <option value="blog_custom_fields">
            {datamodelTypeLookup.blog_custom_fields}
          </option>
          <option value="menu_item_custom_fields">
            {datamodelTypeLookup.menu_item_custom_fields}
          </option>
        </SelectField>
        {datamodelType === 'datamodel_item' && (
          <SelectField
            register={register}
            error={errors.datamodel_sub_type}
            name="datamodel_sub_type"
            options={{}}
            label="Sub type"
            helpText="Special content database item type."
          >
            <option value="">No subtype</option>
            <option value="page_tag">Page tag</option>
          </SelectField>
        )}
      </FormFieldset>
      <fieldset className="sb-form-item sb-form-item-active">
        <legend>Fields</legend>
        {fieldsList}
        <button
          type="button"
          className="sb-btn sb-btn-cancel"
          onClick={() => fieldsArray.append(blankField())}
        >
          Add <PlusIcon />
        </button>
      </fieldset>
      {datamodelData.id && (
        <FormFieldset legend="Advanced">
          <TextField
            register={register}
            error={errors.slug}
            options={{
              required: 'This field is required',
              onChange: slugifyDash,
              onBlur: (e) => {
                slugifyOnBlur(e);
                setValue('slug', e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
            name="slug"
            label="Slug"
          />
        </FormFieldset>
      )}
      <FormPublishingFooter
        disabled={disabled}
        isDirty={isDirty}
        isError={mutation.isError}
        isValid={isValid}
        publishMutation={mutation}
        statusLabel={datamodelData.id ? 'Published' : 'Unsaved'}
        statusTooltip={
          datamodelData.id
            ? 'Current datamodel is published. Changes are saved live immediately'
            : 'This datamodel has not been saved yet'
        }
      />
    </form>
  );
};
